import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TableRow,
  TableCell,
  Chip,
  Skeleton,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  alpha,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ViewListIcon from "@mui/icons-material/ViewList";
import GridViewIcon from "@mui/icons-material/GridView";
import { useAdp } from "../context/AdpContext";
import { useDraftData } from "../context/DraftDataContext";
import { FilterBar } from "../components/FilterBar";
import { DataTableShell, type ColumnDef, type SortState } from "../components/DataTableShell";
import { MobileCard } from "../components/MobileCard";
import { ColumnToggle } from "../components/ColumnToggle";
import { POSITION_COLORS, PAGE_SIZE } from "../constants";
import { useStatusColors } from "../helpers/statusColors";
import { groupDataByPlayer, normalizePlayerName } from "../helpers/playerHelpers";
import { CHART_TOOLTIP_SX } from "../components/charts/chartTheme";
import { EmptyState } from "../components/ui";
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip as RechartsTooltip } from "recharts";

// ── Types ──────────────────────────────────────────────────────────

interface AdpDataPoint {
  date: string;
  adp: number;
}

const BBM_LAUNCH_DATE = "2026-04-27";
const BBM_LAUNCH_LABEL = "BBM Launch";

export interface PlayerTrend {
  id: string;
  name: string;
  position: string;
  team: string;
  history: AdpDataPoint[];
}

// ── Helpers ────────────────────────────────────────────────────────

function adpOnOrBefore(history: AdpDataPoint[], cutoffDate: string): number | null {
  let result: number | null = null;
  for (const h of history) {
    if (h.date <= cutoffDate) result = h.adp;
    else break;
  }
  return result;
}

function daysAgoDate(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

// Use shared POSITION_COLORS (POS_COLORS alias for local use)
const POS_COLORS = POSITION_COLORS;

// ── Delta cell with mini bar ───────────────────────────────────────

function DeltaCell({ delta, maxAbs }: { delta: number | null; maxAbs: number }) {
  const sc = useStatusColors();
  if (delta === null) {
    return (
      <TableCell sx={{ textAlign: "right", minWidth: 90 }}>
        <Box sx={{ color: "text.disabled" }}>—</Box>
      </TableCell>
    );
  }
  const isPositive = delta > 0.05;
  const isNegative = delta < -0.05;
  const color = isPositive ? sc.good : isNegative ? sc.bad : sc.neutral;
  const arrow = isPositive ? "▲" : isNegative ? "▼" : "–";
  const barPct = maxAbs > 0 ? Math.min(Math.abs(delta) / maxAbs, 1) * 100 : 0;
  const display = Math.abs(delta) < 0.05 ? "0.0" : Math.abs(delta).toFixed(1);

  return (
    <TableCell sx={{ textAlign: "right", minWidth: 90 }}>
      <Box sx={{ color, fontWeight: 600, fontSize: 13 }}>
        <span aria-hidden="true">{arrow}</span>{" "}{display}
      </Box>
      <Box sx={{ mt: 0.5, height: 4, bgcolor: "action.hover", borderRadius: 1, overflow: "hidden" }}>
        <Box sx={{
          height: "100%", borderRadius: 1, bgcolor: color, opacity: 0.75,
          width: `${barPct}%`,
          ml: isNegative ? "auto" : 0,
        }} />
      </Box>
    </TableCell>
  );
}

// ── ADP Sparkline ─────────────────────────────────────────────────

function Sparkline({ history, posColor }: { history: AdpDataPoint[]; posColor: string }) {
  if (history.length < 2) {
    return <Box sx={{ width: 80, height: 30, display: "flex", alignItems: "center",
      justifyContent: "center", color: "text.disabled", fontSize: 11 }}>—</Box>;
  }
  // Invert ADP: lower is better so we want it higher on the chart
  const adpVals = history.map(h => h.adp);
  const minAdp = Math.min(...adpVals);
  const maxAdp = Math.max(...adpVals);
  // Enforce a minimum visible range of 5 ADP picks so tiny moves look flat
  const MIN_SPAN = 5;
  const span = Math.max(maxAdp - minAdp, MIN_SPAN);
  const mid = (maxAdp + minAdp) / 2;
  const domainMin = mid - span / 2;
  const domainMax = mid + span / 2;
  // Invert: v = domainMax - adp so lower ADP is higher on chart
  const data = history.map(h => ({ v: domainMax - h.adp, adp: h.adp, date: h.date }));
  const first = adpVals[0];
  const last = adpVals[adpVals.length - 1];
  const dir = last < first ? "rising" : last > first ? "falling" : "flat";
  return (
    <Box
      sx={{ width: 80, height: 30 }}
      role="img"
      aria-label={`ADP trend ${dir}, currently ${last.toFixed(1)}`}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 2, bottom: 2, left: 0, right: 0 }}>
          <YAxis hide domain={[0, domainMax - domainMin]} />
          <RechartsTooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0].payload;
              const dateStr = d.date ? new Date(d.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";
              return (
                <Box sx={{ ...CHART_TOOLTIP_SX, borderRadius: 1, p: 0.75, fontSize: 11, minWidth: undefined, lineHeight: 1.4 }}>
                  <div style={{ fontWeight: 600 }}>ADP: {d.adp}</div>
                  {dateStr && <div style={{ color: "#aaa" }}>{dateStr}</div>}
                </Box>
              );
            }}
          />
          <Line type="monotone" dataKey="v" stroke={posColor} strokeWidth={1.5}
            dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

// ── Reference Date Picker ─────────────────────────────────────────

function ReferenceDatePicker({ dates, selected, onSelect }: {
  dates: string[];
  selected: string;
  onSelect: (d: string) => void;
}) {
  if (dates.length === 0) return null;
  const fmt = (d: string) => {
    const dt = new Date(d + "T12:00:00Z");
    return dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  };
  const latest = dates[dates.length - 1];
  const labelForDate = (d: string) => {
    const isLatest = d === latest;
    const isBbmLaunch = d === BBM_LAUNCH_DATE;
    const base = isLatest ? `Today (${fmt(d)})` : fmt(d);
    if (isBbmLaunch) return `${BBM_LAUNCH_LABEL} (${base})`;
    return base;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "stretch", md: "center" },
        gap: 1,
        mb: 2,
      }}
    >
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, whiteSpace: "nowrap", pt: { md: 0.25 } }}>
        Compare from:
      </Typography>
      <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 240 }, maxWidth: { md: 340 } }}>
        <Select
          value={selected}
          onChange={e => onSelect(e.target.value)}
          displayEmpty
          MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
          sx={{ fontSize: 13 }}
        >
          {dates.map(d => {
            return (
              <MenuItem key={d} value={d} sx={{ fontSize: 13 }}>
                {labelForDate(d)}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      <Typography variant="caption" sx={{ color: "text.disabled", whiteSpace: "nowrap" }}>
        {dates.length} snapshots · updates the "vs" column
      </Typography>
    </Box>
  );
}

// ── Draft Grid View ───────────────────────────────────────────────

const DRAFT_TEAMS = 12;
const DRAFT_ROUNDS = 20;

interface GridPlayer {
  id: string;
  name: string;
  position: string;
  team: string;
  currentAdp: number | null;
  /** Your portfolio exposure % (0–100), for the ownership wash. */
  exposure?: number;
}

function DraftGridView({
  players,
  filterPos,
  filterName,
  onSelectOwned,
}: {
  players: GridPlayer[];
  filterPos: Set<string>;
  filterName: string;
  /** Click-through for players you own (exposure > 0). */
  onSelectOwned?: (name: string) => void;
}) {
  const theme = useTheme();
  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;
  const textDisabled = theme.palette.text.disabled;
  const slots = players.slice(0, DRAFT_TEAMS * DRAFT_ROUNDS);

  // Build 2D grid: grid[round][col] = player | null
  const grid: (GridPlayer | null)[][] = Array.from(
    { length: DRAFT_ROUNDS },
    () => Array<GridPlayer | null>(DRAFT_TEAMS).fill(null)
  );
  slots.forEach((player, idx) => {
    const round = Math.floor(idx / DRAFT_TEAMS);
    const posInRound = idx % DRAFT_TEAMS;
    const col = round % 2 === 0 ? posInRound : DRAFT_TEAMS - 1 - posInRound;
    grid[round][col] = player;
  });

  const hasFilter = filterPos.size > 0 || filterName.trim().length > 0;

  const isHighlighted = (player: GridPlayer | null) => {
    if (!player) return false;
    if (!hasFilter) return true;
    const posOk = filterPos.size === 0 || filterPos.has(player.position);
    const nameOk = !filterName || player.name.toLowerCase().includes(filterName.toLowerCase());
    return posOk && nameOk;
  };

  return (
    <Box sx={{ overflowX: "auto", pb: 2, mt: 1 }}>
      {/* Team column headers */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: `36px repeat(${DRAFT_TEAMS}, minmax(88px, 1fr))`,
        gap: "3px",
        mb: "3px",
        minWidth: 36 + DRAFT_TEAMS * 91,
      }}>
        <Box />
        {Array.from({ length: DRAFT_TEAMS }, (_, i) => (
          <Box key={i} sx={{
            textAlign: "center", fontSize: 10, color: "text.secondary",
            fontWeight: 700, py: 0.5, letterSpacing: 0.3,
          }}>
            Team {i + 1}
          </Box>
        ))}
      </Box>

      {/* Rounds */}
      {grid.map((row, roundIdx) => (
        <Box key={roundIdx} sx={{
          display: "grid",
          gridTemplateColumns: `36px repeat(${DRAFT_TEAMS}, minmax(88px, 1fr))`,
          gap: "3px",
          mb: "3px",
          minWidth: 36 + DRAFT_TEAMS * 91,
        }}>
          {/* Round label */}
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 10, color: "text.disabled", fontWeight: 700,
          }}>
            R{roundIdx + 1}
          </Box>

          {row.map((player, colIdx) => {
            const pickNum = roundIdx % 2 === 0
              ? roundIdx * DRAFT_TEAMS + colIdx + 1
              : roundIdx * DRAFT_TEAMS + (DRAFT_TEAMS - colIdx);

            const posColor = player ? (POS_COLORS[player.position] || "#888") : "transparent";
            const highlighted = isHighlighted(player);
            const dimmed = hasFilter && !highlighted;

            // Ownership wash: green intensity scales with your exposure so the
            // board reads as a portfolio map. Text flips white when heavy.
            const exp = player?.exposure ?? 0;
            const owned = exp > 0;
            const heavy = exp >= 30;
            const washBg = owned
              ? alpha(theme.palette.primary.main, Math.min(0.08 + (exp / 100) * 0.9, 0.6))
              : player ? "background.paper" : "action.hover";
            const nameColor = heavy ? "#fff" : textPrimary;
            const metaColor = heavy ? "rgba(255,255,255,0.78)" : textSecondary;

            const clickable = owned && !!onSelectOwned && !!player;
            return (
              <Box
                key={colIdx}
                onClick={clickable ? () => onSelectOwned!(player!.name) : undefined}
                sx={{
                  borderLeft: `3px solid ${player ? posColor : "transparent"}`,
                  bgcolor: washBg,
                  borderRadius: "4px",
                  p: "5px 6px",
                  minHeight: 54,
                  opacity: dimmed ? 0.2 : 1,
                  transition: "opacity 0.15s",
                  boxShadow: highlighted && hasFilter ? `0 0 0 1px ${posColor}` : "none",
                  ...(clickable && { cursor: "pointer", "&:hover": { filter: "brightness(1.06)" } }),
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.25 }}>
                  <Typography sx={{ fontSize: 9, fontWeight: 600, lineHeight: 1, color: heavy ? "rgba(255,255,255,0.7)" : textDisabled }}>
                    #{pickNum}
                  </Typography>
                  {owned && (
                    <Typography sx={{ fontSize: 9, fontWeight: 700, lineHeight: 1, color: nameColor, fontVariantNumeric: "tabular-nums" }}>
                      {Math.round(exp)}%
                    </Typography>
                  )}
                </Box>
                {player ? (
                  <>
                    <Typography sx={{
                      fontSize: 11, fontWeight: 700, lineHeight: 1.25, color: nameColor,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {player.name}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", mt: 0.25 }}>
                      <Chip
                        label={player.position}
                        size="small"
                        sx={{
                          fontSize: 9, height: 14, bgcolor: posColor, color: "#fff",
                          fontWeight: 700, borderRadius: "3px",
                          "& .MuiChip-label": { px: 0.5 },
                        }}
                      />
                      <Typography sx={{ fontSize: 10, color: metaColor }}>
                        {player.team}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 0.5 }}>—</Typography>
                )}
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}

// ── Page ──────────────────────────────────────────────────────────

type SortCol = "name" | "currentAdp" | "delta7" | "delta14" | "deltaFirst";

const ADP_TRENDS_COLUMNS: ColumnDef[] = [
  { id: "name", label: "Player", sortable: true, minWidth: 160 },
  { id: "position", label: "Pos", width: 60 },
  { id: "team", label: "Team", width: 70 },
  { id: "trend", label: "Trend", width: 90 },
  { id: "currentAdp", label: "Current ADP", sortable: true, width: 110, align: "right" },
  { id: "delta7", label: "vs 7d", sortable: true, minWidth: 90, align: "right" },
  { id: "delta14", label: "vs 14d", sortable: true, minWidth: 90, align: "right" },
  { id: "deltaFirst", label: "vs chosen date", sortable: true, minWidth: 120, align: "right" },
];

const POSITIONS = ["QB", "RB", "WR", "TE"];

export default function AdpTrendsPage() {
  const { adpHistory, adpHistoryLoading, snapshotDates } = useAdp();
  const { teamFilter, filteredRawData } = useDraftData();
  const navigate = useNavigate();

  // Your portfolio exposure per player, for the draft-grid ownership wash.
  const exposureByName = useMemo(() => {
    const m = new Map<string, number>();
    const totalDrafts = new Set(filteredRawData.map(r => r["Draft"])).size || 1;
    for (const p of groupDataByPlayer(filteredRawData)) {
      const drafts = new Set(p.rows.map(r => r["Draft"])).size;
      m.set(normalizePlayerName(p.name), (drafts / totalDrafts) * 100);
    }
    return m;
  }, [filteredRawData]);
  const theme = useTheme();
  const sc = useStatusColors();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [filterName, setFilterName] = useState("");
  const [filterPos, setFilterPos] = useState<Set<string>>(new Set());
  const [sortCol, setSortCol] = useState<SortCol>("currentAdp");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [referenceDate, setReferenceDate] = useState<string>("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(
    new Set(ADP_TRENDS_COLUMNS.map(c => c.id))
  );
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");

  // Default reference date to BBM launch when available; otherwise use earliest snapshot.
  useEffect(() => {
    if (snapshotDates.length === 0) return;

    const preferredDefault = snapshotDates.includes(BBM_LAUNCH_DATE)
      ? BBM_LAUNCH_DATE
      : snapshotDates[0];

    if (referenceDate === "" || !snapshotDates.includes(referenceDate)) {
      setReferenceDate(preferredDefault);
    }
  }, [snapshotDates, referenceDate]);

  // Reset visible count when filters/sort change
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [filterName, filterPos, sortCol, sortDir]);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const ago7  = useMemo(() => daysAgoDate(7), []);
  const ago14 = useMemo(() => daysAgoDate(14), []);

  const rows = useMemo(() => {
    return adpHistory.map(p => {
      const history = p.history;
      const currentAdp  = adpOnOrBefore(history, today);
      const adp7        = adpOnOrBefore(history, ago7);
      const adp14       = adpOnOrBefore(history, ago14);
      const adpRef      = referenceDate ? adpOnOrBefore(history, referenceDate) : null;
      const delta7      = currentAdp !== null && adp7   !== null ? adp7   - currentAdp : null;
      const delta14     = currentAdp !== null && adp14  !== null ? adp14  - currentAdp : null;
      const deltaFirst  = currentAdp !== null && adpRef !== null ? adpRef - currentAdp : null;
      return { ...p, currentAdp, delta7, delta14, deltaFirst };
    });
  }, [adpHistory, today, ago7, ago14, referenceDate]);

  const teamFilteredRows = useMemo(() => {
    if (!teamFilter) return rows;
    return rows.filter(p => p.team === teamFilter);
  }, [rows, teamFilter]);

  // All players sorted by ADP for the draft grid (ignores position/name filters)
  const gridRows = useMemo(() => {
    return teamFilteredRows
      .filter(p => p.currentAdp !== null)
      .sort((a, b) => (a.currentAdp ?? 999) - (b.currentAdp ?? 999))
      .map(p => ({ ...p, exposure: exposureByName.get(normalizePlayerName(p.name)) }));
  }, [teamFilteredRows, exposureByName]);

  const filtered = useMemo(() => {
    return teamFilteredRows.filter(p => {
      if (p.currentAdp === null) return false;
      if (filterName && !p.name.toLowerCase().includes(filterName.toLowerCase())) return false;
      if (filterPos.size > 0 && !filterPos.has(p.position)) return false;
      return true;
    });
  }, [teamFilteredRows, filterName, filterPos]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aVal: any = a[sortCol];
      const bVal: any = b[sortCol];
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      if (typeof aVal === "string") {
        return sortDir === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [filtered, sortCol, sortDir]);

  const paginated = useMemo(() => sorted.slice(0, visibleCount), [sorted, visibleCount]);

  const maxAbs7 = useMemo(() =>
    Math.max(...filtered.map(p => Math.abs(p.delta7 ?? 0)), 0.1), [filtered]);
  const maxAbs14 = useMemo(() =>
    Math.max(...filtered.map(p => Math.abs(p.delta14 ?? 0)), 0.1), [filtered]);
  const maxAbsFirst = useMemo(() =>
    Math.max(...filtered.map(p => Math.abs(p.deltaFirst ?? 0)), 0.1), [filtered]);

  const handleSort = (col: string) => {
    const c = col as SortCol;
    if (sortCol === c) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(c); setSortDir("asc"); }
  };

  const togglePosition = (pos: string) => {
    setFilterPos(prev => {
      const next = new Set(prev);
      if (next.has(pos)) next.delete(pos);
      else next.add(pos);
      return next;
    });
  };

  const sortState: SortState = { key: sortCol, dir: sortDir };

  // Dynamic "vs ref date" column label
  const displayColumns: ColumnDef[] = ADP_TRENDS_COLUMNS
    .filter(c => visibleColumns.has(c.id))
    .map(c => {
      if (c.id !== "deltaFirst") return c;
      const fmt = referenceDate
        ? new Date(referenceDate + "T12:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric" })
        : "chosen date";
      return { ...c, label: `vs ${fmt}` };
    });

  if (adpHistoryLoading) {
    return (
      <Box sx={{ px: 3, mt: 2 }}>
        {Array.from({ length: 12 }).map((_, i) => (
          <Skeleton key={i} height={44} sx={{ mb: 0.5 }} />
        ))}
      </Box>
    );
  }

  if (!adpHistory.length) {
    return (
      <EmptyState
        icon={<TrendingUpIcon />}
        title="No ADP history yet"
        hint="ADP data loads automatically. Check back after the first sync."
      />
    );
  }

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
      {/* Source label — ADP Trends is Underdog market data only. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
        <Chip size="small" variant="outlined" label="Underdog ADP" sx={{ color: "text.secondary", borderColor: "divider" }} />
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Underdog market ADP movement over time.
        </Typography>
      </Box>

      {/* Reference date picker — controls the last delta column */}
      <ReferenceDatePicker
        dates={snapshotDates}
        selected={referenceDate}
        onSelect={setReferenceDate}
      />

      {/* Shared filter bar */}
      <FilterBar
        filterName={filterName}
        onFilterName={setFilterName}
        positions={POSITIONS}
        selectedPositions={filterPos}
        onTogglePosition={togglePosition}
        activeFilterCount={(filterName ? 1 : 0) + filterPos.size + (teamFilter ? 1 : 0)}
        onReset={() => { setFilterName(""); setFilterPos(new Set()); }}
        resultText={
          viewMode === "grid"
            ? `${gridRows.length} players · 20 rounds · 12 teams · snake`
            : `${sorted.length} players · ▲ rose · ▼ fell`
        }
        actions={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {viewMode === "list" && (
              <ColumnToggle
                columns={ADP_TRENDS_COLUMNS}
                visibleColumns={visibleColumns}
                onChange={setVisibleColumns}
              />
            )}
            <Tooltip title={viewMode === "list" ? "Draft grid view" : "List / table view"}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_e, val) => { if (val) setViewMode(val); }}
                size="small"
                sx={{ "& .MuiToggleButton-root": { py: 0.5, px: 0.75 } }}
              >
                <ToggleButton value="list" aria-label="List view">
                  <ViewListIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="grid" aria-label="Draft grid view">
                  <GridViewIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </Tooltip>
          </Box>
        }
      />

      {/* Draft grid view */}
      {viewMode === "grid" ? (
        <DraftGridView
          players={gridRows}
          filterPos={filterPos}
          filterName={filterName}
          onSelectOwned={name => navigate(`/players?q=${encodeURIComponent(name)}`)}
        />
      ) : isMobile ? (
        <Box sx={{ py: 1.5 }}>
          {paginated.map(p => {
            const posColor = POS_COLORS[p.position] || "#888";
            return (
              <MobileCard
                key={p.id}
                accentColor={posColor}
                expanded={false}
                onToggle={() => {}}
                header={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                    <Chip label={p.position} size="small" sx={{
                      fontSize: 11, bgcolor: posColor, color: "#fff", fontWeight: 700,
                      height: 22, borderRadius: 1,
                    }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 14, whiteSpace: "nowrap",
                        overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</Typography>
                      <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{p.team}</Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
                      {p.currentAdp?.toFixed(1)}
                    </Typography>
                  </Box>
                }
                summary={
                  <Box sx={{ display: "flex", gap: 2.5, pt: 1 }}>
                    {[
                      { label: "vs 7d", delta: p.delta7, maxAbs: maxAbs7 },
                      { label: "vs 14d", delta: p.delta14, maxAbs: maxAbs14 },
                      { label: `vs ${referenceDate || "ref"}`, delta: p.deltaFirst, maxAbs: maxAbsFirst },
                    ].map(({ label, delta, maxAbs }) => {
                      const color = delta === null ? "text.disabled"
                        : delta > 0.05 ? sc.good
                        : delta < -0.05 ? sc.bad
                        : "#6B7280";
                      const arrow = delta === null ? "—"
                        : delta > 0.05 ? "▲"
                        : delta < -0.05 ? "▼"
                        : "–";
                      return (
                        <Box key={label}>
                          <Typography sx={{ fontSize: 10, color: "text.secondary", fontWeight: 500 }}>{label}</Typography>
                          <Typography sx={{ fontSize: 13, color, fontWeight: 600 }}>
                            {delta === null ? "—" : `${arrow} ${Math.abs(delta).toFixed(1)}`}
                          </Typography>
                        </Box>
                      );
                    })}
                    <Box sx={{ ml: "auto" }}>
                      <Typography sx={{ fontSize: 10, color: "text.secondary", fontWeight: 500 }}>Trend</Typography>
                      <Sparkline history={p.history} posColor={posColor} />
                    </Box>
                  </Box>
                }
              />
            );
          })}
          {paginated.length === 0 && (
            <Box sx={{ textAlign: "center", py: 6, color: "text.disabled" }}>
              No players match your filters.
            </Box>
          )}
          {visibleCount < sorted.length && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <Button variant="outlined" onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}>
                Show more ({sorted.length - visibleCount} remaining)
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        /* Desktop table shell */
        <DataTableShell
          columns={displayColumns}
          items={paginated}
          totalCount={sorted.length}
          visibleCount={visibleCount}
          onShowMore={() => setVisibleCount(c => c + PAGE_SIZE)}
          sortState={sortState}
          onSort={handleSort}
          tableLabel="ADP trends table"
          emptyState={
            <EmptyState dense icon={<TrendingUpIcon />} title="No players match your filters" />
          }
          renderRow={p => (
            <TableRow key={p.id}>
              {visibleColumns.has("name") && (
                <TableCell sx={{ fontWeight: 500 }}>{p.name}</TableCell>
              )}
              {visibleColumns.has("position") && (
                <TableCell>
                  <Chip label={p.position} size="small" sx={{
                    fontSize: 11, bgcolor: POS_COLORS[p.position] ?? "#888",
                    color: "#fff", fontWeight: 700, height: 22, borderRadius: 1,
                  }} />
                </TableCell>
              )}
              {visibleColumns.has("team") && (
                <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>{p.team}</TableCell>
              )}
              {visibleColumns.has("trend") && (
                <TableCell sx={{ p: 0.5 }}>
                  <Sparkline history={p.history} posColor={POS_COLORS[p.position] || "#888"} />
                </TableCell>
              )}
              {visibleColumns.has("currentAdp") && (
                <TableCell sx={{ textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                  {p.currentAdp?.toFixed(1)}
                </TableCell>
              )}
              {visibleColumns.has("delta7") && (
                <DeltaCell delta={p.delta7} maxAbs={maxAbs7} />
              )}
              {visibleColumns.has("delta14") && (
                <DeltaCell delta={p.delta14} maxAbs={maxAbs14} />
              )}
              {visibleColumns.has("deltaFirst") && (
                <DeltaCell delta={p.deltaFirst} maxAbs={maxAbsFirst} />
              )}
            </TableRow>
          )}
        />
      )}
    </Box>
  );
}
